namespace Solovis.UI.InputControls {


    /**
     * FormEntryFactoryManager manages the set of standard editors that are allowed within FormBuilder.addEntry for editor type.
     */
    export class FormEntryFactoryManager {

        public static standardTextAlignTopPadding = "10px";
        public static standardTextAlignTopPaddingPixels = 10;


        public static tryGetFactoryForType<Values = DefaultValueShape>(editorType: string): (editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>) => FormEntryEditorData {
            let editorFactory = FormEntryFactoryManager._editorTypeToFactoryMap[editorType];
            if (editorFactory) {
                return editorFactory;
            }

            return null;
        }

        private static _createEditorForBool<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            createEditorOptions.container.css("padding-top", "7px");
            let checkDiv = $("<div>").appendTo(createEditorOptions.container);

            let isDisabled = (editorOptions.checkBoxOptions && editorOptions.checkBoxOptions.disabled) ? true : false;
            $(checkDiv).dxCheckBox({
                value: createEditorOptions.initialValue,

                disabled: isDisabled,
                onValueChanged: function () {
                    $(editor).trigger("valueChanged");
                }
            });

            let checkBox = $(checkDiv).dxCheckBox("instance");

            let editor = {
                getValue: function () {
                    return !!checkBox.option("value");
                },
                setValue: function (newValue) {
                    return checkBox.option("value", !!newValue);
                },
            };

            return editor;
        }

        private static _createEditorForText<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            var editorControl = new Solovis.UI.InputControl({
                container: createEditorOptions.container,
                width: (editorOptions.textOptions && editorOptions.textOptions.width) ? editorOptions.textOptions.width : "100%",
                max: (editorOptions.textOptions && editorOptions.textOptions.maxLength) ? editorOptions.textOptions.maxLength : null,
                type: (editorOptions.textOptions && editorOptions.textOptions.isPassword) ? "password": "text",
                value: createEditorOptions.initialValue,
                required: !!editorOptions.required,
                validationModel: createEditorOptions.validationModel,
                fireValueChangedOnKeyup: editorOptions.textOptions ? editorOptions.textOptions.fireValueChangedOnKeyup : null
            });

            return editorControl;
        }

        private static _createEditorForSelect<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            let selectOptions = editorOptions.selectOptions;

            let dataSource: any = selectOptions ? selectOptions.data : null;
            if (selectOptions && selectOptions.dataSource) {
                dataSource = selectOptions.dataSource;
            }

            createEditorOptions.container.css("padding-bottom", "5px");
            let selectDiv = $("<div>").appendTo(createEditorOptions.container);
            $(selectDiv).dxSelectBox({
                displayExpr: "text",
                valueExpr: "value",
                dataSource: dataSource,
                searchEnabled: selectOptions ? !!selectOptions.searchEnabled : false,
                searchMode: "contains",
                showClearButton: selectOptions ? !!selectOptions.showClearButton : false,
                acceptCustomValue: selectOptions ? !!selectOptions.acceptCustomValue : false,
                onCustomItemCreating: (selectOptions && selectOptions.createCustomItemCallback) ?
                    function (e) {
                        let text = e.text;
                        let customItemResult = selectOptions.createCustomItemCallback(text);
                        return customItemResult;
                    }
                    :
                    null,
                value: createEditorOptions.initialValue,

                onValueChanged: function (e) {
                    $(editor).trigger("valueChanged");
                }
            });

            let selectBox = $(selectDiv).dxSelectBox("instance");

            let editor = {
                getValue: function () {
                    let value = selectBox.option("value");
                    return value;
                },
                setValue: function (newValue) {
                    selectBox.option("value", newValue);
                }
            };

            return editor;
        }

        private static _createEditorForMultiselect<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            var multiselectOptions = editorOptions.multiselectOptions;

            let dataSource: any = multiselectOptions ? multiselectOptions.data : null;
            if (multiselectOptions && multiselectOptions.dataSource) {
                dataSource = multiselectOptions.dataSource;
            }

            createEditorOptions.container.css("padding-bottom", "5px");
            let selectDiv = $("<div>").appendTo(createEditorOptions.container);
            $(selectDiv).dxTagBox({
                displayExpr: "text",
                valueExpr: "value",
                dataSource: dataSource,
                searchEnabled: multiselectOptions ? !!multiselectOptions.searchEnabled : false,
                searchMode: "contains",
                showClearButton: multiselectOptions ? !!multiselectOptions.showClearButton : false,
                acceptCustomValue: multiselectOptions ? !!multiselectOptions.acceptCustomValue : false,
                onCustomItemCreating: (multiselectOptions && multiselectOptions.createCustomItemCallback) ?
                    function (e) {
                        let text = e.text;
                        let customItemResult = multiselectOptions.createCustomItemCallback(text);
                        return customItemResult;
                    }
                    :
                    null,
                value: createEditorOptions.initialValue,

                onValueChanged: function (e) {
                    $(editor).trigger("valueChanged");
                }
            });

            let tagBox = $(selectDiv).dxTagBox("instance");

            let editor = {
                getValue: function () {
                    let value = tagBox.option("value");
                    return value;
                },
                setValue: function (newValue) {
                    tagBox.option("value", newValue);
                }
            };

            return editor;
        }

        private static _createEditorForColor<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            createEditorOptions.container.css("padding-bottom", "5px");

            let colorBoxOptions: any = {
                value: createEditorOptions.initialValue,

                onValueChanged: function (e) {
                    $(editor).trigger("valueChanged");
                }
            };

            if (editorOptions.colorOptions && editorOptions.colorOptions.editAlphaChannel) {
                colorBoxOptions.editAlphaChannel = true;
            }

            let colorBoxDiv = $("<div>").appendTo(createEditorOptions.container);
            let colorBox = ($(colorBoxDiv) as any).dxColorBox(colorBoxOptions);

            let editor = {
                getValue: function () {
                    let color = colorBox.dxColorBox("instance").option("value");
                    return color ? color : null;
                },
                setValue: function (newValue) {
                    colorBox.dxColorBox("instance").option("value", newValue);
                }
            };

            return editor;
        }

        private static _createEditorForParameter<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            createEditorOptions.container.css("padding-top", FormEntryFactoryManager.standardTextAlignTopPadding);

            let portalServices = editorOptions.parameterOptions.portalServices;
            if (!portalServices) {
                portalServices = createEditorOptions.formBuilder.portalServices();
            }

            let editorControl = new Solovis.UI.InputControls.ParameterInputControl({
                controlHost: createEditorOptions.container,
                parameterType: editorOptions.parameterOptions.parameterType,
                value: createEditorOptions.initialValue,
                required: !!editorOptions.required,
                validationModel: createEditorOptions.validationModel,
                settings: editorOptions.parameterOptions.settings,
                portalServices: portalServices,
                renderMode: 'classic'
            });

            editorControl.setAsCurrentControl();

            return editorControl;
        }

        private static _createEditorForNumberAsString<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            var editorControl = new Solovis.UI.InputControl({
                container: createEditorOptions.container,
                width: (editorOptions.numberAsStringOptions && Solovis.isNotNull(editorOptions.numberAsStringOptions.width)) ? editorOptions.numberAsStringOptions.width : 210,
                type: "number",
                decimals: editorOptions.numberAsStringOptions ? editorOptions.numberAsStringOptions.decimals : null,
                showPercentSign: editorOptions.numberAsStringOptions ? editorOptions.numberAsStringOptions.showPercentSign : null,
                treatBlankAsZero: editorOptions.numberAsStringOptions ? !!editorOptions.numberAsStringOptions.treatBlankAsZero : false,
                value: createEditorOptions.initialValue,
                validationModel: createEditorOptions.validationModel,
                fireValueChangedOnKeyup: editorOptions.numberAsStringOptions ? editorOptions.numberAsStringOptions.fireValueChangedOnKeyup : null,
                required: !!editorOptions.required
            });

            return editorControl;
        }


        private static _createEditorForInteger<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            var editorControl = new Solovis.UI.InputControl({
                container: createEditorOptions.container,
                width: (editorOptions.integerOptions && Solovis.isNotNull(editorOptions.integerOptions.width)) ? editorOptions.integerOptions.width : 210,
                type: "number",
                decimals: 0,
                showPercentSign: editorOptions.integerOptions ? editorOptions.integerOptions.showPercentSign : null,
                treatBlankAsZero: editorOptions.integerOptions ? !!editorOptions.integerOptions.treatBlankAsZero : false,
                value: createEditorOptions.initialValue,
                validationModel: createEditorOptions.validationModel,
                fireValueChangedOnKeyup: editorOptions.integerOptions ? editorOptions.integerOptions.fireValueChangedOnKeyup : null,
                required: !!editorOptions.required
            });

            let editor = FormEntryFactoryManager.WrapEditor(editorControl);

            let originalGetValue = editor.getValue;
            let originalSetValue = editor.setValue;

            editor.getValue = function () {
                let strValue = originalGetValue();
                
                if (Solovis.isNotNull(strValue) && strValue.trim() != '') {
                    let intValue = parseInt(strValue);
                    return intValue;
                }
                else {
                    return null;
                }
            };

            editor.setValue = function (newValue) {                
                originalSetValue(newValue);
            };

            return editor;            
        }


        private static _createEditorForTextarea<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            let textarea = $("<textarea>").appendTo(createEditorOptions.container);

            if (editorOptions.textareaOptions) {
                if (editorOptions.textareaOptions.width) {
                    textarea.css("width", editorOptions.textareaOptions.width);
                }
                if (editorOptions.textareaOptions.height) {
                    textarea.css("height", editorOptions.textareaOptions.height);
                }
            }

            if (createEditorOptions.initialValue) {
                textarea.val(createEditorOptions.initialValue);
            }

            let editor = {
                getValue: function () {
                    let textValue = textarea.val();
                    if (editorOptions.textareaOptions && editorOptions.textareaOptions.trim) {
                        textValue = textValue.trim();
                    }
                    textValue = textValue ? textValue : null;
                    return textValue;
                },
                setValue: function (newValue) {
                    textarea.val(newValue);
                },
                setWidth: function (newWidth) {
                    textarea.outerWidth(newWidth);
                }
            };

            return editor;
        }

        private static _createEditorForDayId<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {
            let getDateForValue = function (incomingValue) {
                if (incomingValue instanceof Date) {
                    return incomingValue;
                }
                else if (typeof incomingValue === "number") {
                    return InvData.DayId.toDate(incomingValue);
                }
                return null;
            };

            let editorControl = new Solovis.UI.InputControl({
                container: createEditorOptions.container,
                type: "date",
                value: getDateForValue(createEditorOptions.initialValue),
                validationModel: createEditorOptions.validationModel,
                required: !!editorOptions.required,
                monthEnd: editorOptions.dayIdOptions ? editorOptions.dayIdOptions.monthEnd : null                
            });

            var editor = FormEntryFactoryManager.WrapEditor(editorControl);

            let originalGetValue = editor.getValue;
            let originalSetValue = editor.setValue;

            editor.getValue = function () {
                let dateValue = originalGetValue();
                if (dateValue)
                    return InvData.DayId.forDate(dateValue);
                else
                    return null;
            };

            editor.setValue = function (newValue) {
                let dateValue = getDateForValue(newValue);
                originalSetValue(dateValue);
            };

            return editor;
        }

        private static _createEditorForUtcTimestamp<Values = DefaultValueShape>(editorOptions: FormEditorOptions<Values>, createEditorOptions: FormCreateEditorOptions<Values>): FormEntryEditorData {            



            let editorControl = new Solovis.UI.InputControl({
                container: createEditorOptions.container,
                type: "utcTimestamp",
                utcDisplayMode: (editorOptions.utcTimestampOptions && Solovis.isNotNull(editorOptions.utcTimestampOptions.displayMode)) ? editorOptions.utcTimestampOptions.displayMode : "utc", 
                value: createEditorOptions.initialValue,
                validationModel: createEditorOptions.validationModel,
                required: !!editorOptions.required                
            });

            var editor = FormEntryFactoryManager.WrapEditor(editorControl);

            return editor;
        }
        


        /**
         * WrapEditor will wrap the underlying editor with an object that provides a shim layer down
         * to it, so that other methods can be overridden.
         * @param underlyingEditor
         */
        public static WrapEditor(underlyingEditor): FormEntryEditorData {
            let editor: FormEntryEditorData = {
                __isFormEntryEditorDataWrapper: true
            };

            if (underlyingEditor.getValue) {
                editor.getValue = function () {
                    return underlyingEditor.getValue();
                };
            }

            if (underlyingEditor.setValue) {
                editor.setValue = function (newValue) {
                    underlyingEditor.setValue(newValue);
                };
            }

            if (underlyingEditor.isValid) {
                editor.isValid = function () {
                    return underlyingEditor.isValid();
                };
            }

            if (underlyingEditor.focus) {
                editor.focus = function () {
                    underlyingEditor.focus();
                };
            }

            if (underlyingEditor.setWidth) {
                editor.setWidth = function (newWidth) {
                    underlyingEditor.setWidth(newWidth);
                };
            }

            $(underlyingEditor).bind("valueChanged", function () {
                $(editor).trigger("valueChanged");
            });

            return editor;
        }



        private static _editorTypeToFactoryMap: any = {
            "bool": FormEntryFactoryManager._createEditorForBool,
            "text": FormEntryFactoryManager._createEditorForText,
            "select": FormEntryFactoryManager._createEditorForSelect,
            "color": FormEntryFactoryManager._createEditorForColor,
            "parameter": FormEntryFactoryManager._createEditorForParameter,
            "numberAsString": FormEntryFactoryManager._createEditorForNumberAsString,
            "integer": FormEntryFactoryManager._createEditorForInteger,
            "textarea": FormEntryFactoryManager._createEditorForTextarea,
            "dayId": FormEntryFactoryManager._createEditorForDayId,
            "utcTimestamp": FormEntryFactoryManager._createEditorForUtcTimestamp,
            "multiselect": FormEntryFactoryManager._createEditorForMultiselect
        };
    }

}